import { useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { mockUser, mockClients } from '@/mocks/data';

export default function BillingSettingsPage() {
  const [defaultRate, setDefaultRate] = useState((mockUser.hourlyRate || 0).toString());
  const [clientRates, setClientRates] = useState(
    mockClients
      .filter((client) => client.hourlyRate !== undefined)
      .map((client) => ({
        clientId: client.id,
        clientName: client.name,
        rate: (client.hourlyRate || 0).toString(),
      }))
  );

  const [invoiceSettings, setInvoiceSettings] = useState({
    invoicePrefix: 'INV',
    invoiceNumber: '1001',
    dueDays: '30',
    lateFeePercent: '2.5',
    currency: 'USD',
    taxRate: '8.25',
    includeTimeLogs: true,
    logoUrl: '',
    footerText: 'Thank you for your business!',
  });

  const [paymentMethods, setPaymentMethods] = useState([
    {
      id: '1',
      type: 'Bank Transfer',
      details: 'Chase Bank - ***1234',
      isDefault: true,
    },
    {
      id: '2',
      type: 'PayPal',
      details: 'alex@flowky.com',
      isDefault: false,
    },
  ]);

  const handleDefaultRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDefaultRate(e.target.value);
  };

  const handleClientRateChange = (clientId: string, rate: string) => {
    setClientRates((prev) =>
      prev.map((client) => (client.clientId === clientId ? { ...client, rate } : client))
    );
  };

  const handleInvoiceSettingChange = (field: string, value: string | boolean) => {
    setInvoiceSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    console.log('Saving billing settings:', {
      defaultRate,
      clientRates,
      invoiceSettings,
      paymentMethods,
    });
  };

  const addPaymentMethod = () => {
    console.log('Adding new payment method');
  };

  const removePaymentMethod = (id: string) => {
    setPaymentMethods((prev) => prev.filter((method) => method.id !== id));
  };

  const setDefaultPaymentMethod = (id: string) => {
    setPaymentMethods((prev) =>
      prev.map((method) => ({
        ...method,
        isDefault: method.id === id,
      }))
    );
  };

  return (
    <AppLayout>
      <Breadcrumb
        items={[{ label: 'Settings', href: '/settings' }, { label: 'Billing & Rates' }]}
      />

      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Billing & Rates</h1>
            <p className="text-gray-600">Configure your billing preferences and hourly rates</p>
          </div>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors"
          >
            Save Changes
          </button>
        </div>

        <div className="space-y-6">
          {/* Default Hourly Rate */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Default Hourly Rate</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Default Rate</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                    $
                  </span>
                  <input
                    type="number"
                    value={defaultRate}
                    onChange={handleDefaultRateChange}
                    min="0"
                    step="0.01"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  This rate will be used for new clients and projects
                </p>
              </div>
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold text-flowky-primary">${defaultRate}</div>
                  <div className="text-sm text-gray-600">per hour</div>
                </div>
              </div>
            </div>
          </div>

          {/* Client-Specific Rates */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Client-Specific Rates</h3>
            <p className="text-sm text-gray-600 mb-4">
              Override the default rate for specific clients
            </p>
            <div className="space-y-4">
              {clientRates.map((client) => (
                <div
                  key={client.clientId}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div>
                    <div className="font-medium text-gray-900">{client.clientName}</div>
                    <div className="text-sm text-gray-600">Custom hourly rate</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">$</span>
                    <input
                      type="number"
                      value={client.rate}
                      onChange={(e) => handleClientRateChange(client.clientId, e.target.value)}
                      min="0"
                      step="0.01"
                      className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                    />
                    <span className="text-gray-500">/hr</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Invoice Settings */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Prefix
                </label>
                <input
                  type="text"
                  value={invoiceSettings.invoicePrefix}
                  onChange={(e) => handleInvoiceSettingChange('invoicePrefix', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Next Invoice Number
                </label>
                <input
                  type="text"
                  value={invoiceSettings.invoiceNumber}
                  onChange={(e) => handleInvoiceSettingChange('invoiceNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Due (Days)
                </label>
                <select
                  value={invoiceSettings.dueDays}
                  onChange={(e) => handleInvoiceSettingChange('dueDays', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                >
                  <option value="15">15 days</option>
                  <option value="30">30 days</option>
                  <option value="45">45 days</option>
                  <option value="60">60 days</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Late Fee (%)</label>
                <input
                  type="number"
                  value={invoiceSettings.lateFeePercent}
                  onChange={(e) => handleInvoiceSettingChange('lateFeePercent', e.target.value)}
                  min="0"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                <select
                  value={invoiceSettings.currency}
                  onChange={(e) => handleInvoiceSettingChange('currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="CAD">CAD (C$)</option>
                  <option value="AUD">AUD (A$)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tax Rate (%)</label>
                <input
                  type="number"
                  value={invoiceSettings.taxRate}
                  onChange={(e) => handleInvoiceSettingChange('taxRate', e.target.value)}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Footer Text
                </label>
                <textarea
                  value={invoiceSettings.footerText}
                  onChange={(e) => handleInvoiceSettingChange('footerText', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="includeTimeLogs"
                    checked={invoiceSettings.includeTimeLogs}
                    onChange={(e) =>
                      handleInvoiceSettingChange('includeTimeLogs', e.target.checked)
                    }
                    className="w-4 h-4 text-flowky-primary border-gray-300 rounded focus:ring-flowky-primary"
                  />
                  <label htmlFor="includeTimeLogs" className="text-sm font-medium text-gray-700">
                    Include time logs in invoice details
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Payment Methods</h3>
              <button
                onClick={addPaymentMethod}
                className="px-3 py-1 text-sm bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors"
              >
                Add Method
              </button>
            </div>
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      {method.type === 'Bank Transfer' ? (
                        <svg
                          className="w-5 h-5 text-gray-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5 text-gray-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                          />
                        </svg>
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        {method.type}
                        {method.isDefault && (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">{method.details}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!method.isDefault && (
                      <button
                        onClick={() => setDefaultPaymentMethod(method.id)}
                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-white/30 transition-colors"
                      >
                        Set Default
                      </button>
                    )}
                    <button
                      onClick={() => removePaymentMethod(method.id)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Billing Analytics */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing Analytics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">$12,450</div>
                <div className="text-sm text-gray-600">This Month</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-flowky-secondary">$8,230</div>
                <div className="text-sm text-gray-600">Outstanding</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">$2,100</div>
                <div className="text-sm text-gray-600">Overdue</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
